'use client';
import React, { useState } from 'react';
import Image from 'next/image';

interface CourseHighlight {
  id: string;
  highlight: string;
}

interface Course {
  id: number;
  title: string;
  duration: string;
  shortDescription: string;
  highlights: CourseHighlight[];
  imageUrl: string;
  imageAlt: string;
  index: number;
  twinPrice: string;
  singlePrice: string;
  fullDescription: string[];
}

const courses: Course[] = [
{
  id: 3,
  title: 'Yoga Teacher Training - 200 hrs [with free Singing Bowls basics]',
  duration: '200 Hours',
  shortDescription: 'Yoga Alliance Certified (RYT) Hatha Yoga Teacher Training combining ancient traditions with contemporary Training of Trainers (ToT) frameworks.',
  highlights: [
  { id: '1', highlight: 'Yoga Alliance Certified (RYT 200)' },
  { id: '2', highlight: 'Integrated Hatha Yoga Postures & Alignment' },
  { id: '3', highlight: 'Functional Anatomy & Physiology for Safety' },
  { id: '4', highlight: 'Training of Trainers (ToT) Professional Skills' },
  { id: '5', highlight: 'Daily Pranayama & Meditation Sessions' },
  { id: '6', highlight: 'Bonus: Mindfulness, Mind-Management & Yoga Marketing' }],

  imageUrl: 'https://www.zenyoganepal.com/api/media/file/20260308_09bm%2Cm%2C3650.jpg?prefix=uploads',
  imageAlt: 'Yoga 200',
  index: 1,
  twinPrice: '$999',
  singlePrice: '$1550',
  fullDescription: [
  'Our 200-Hour Hatha Yoga Teacher Training follows an integrated learning approach that combines traditional yogic knowledge with modern instructional excellence.',
  'You will study classical Hatha Yoga postures through: Alignment, Biomechanics, Modifications and Intelligent sequencing suitable for beginners & intermediate practitioners. Daily Pranayama sessions will cover breath awareness, cleansing techniques, energy management, and guided meditation.',
  "Unlike conventional teacher trainings, our curriculum incorporates 'Professional Teacher Development' inspired by modern Training of Trainers (ToT) frameworks."]

},
{
  id: 4,
  title: 'Buddha Bowls TTC - 100 hrs [with free Yoga basics]',
  duration: '100 Hours',
  shortDescription: "Experience Nepal's timeless tradition of Himalayan sound healing and become a certified Singing Bowls healer and teacher.",
  highlights: [
  { id: '1', highlight: 'Authentic Tibetan Bowl techniques' },
  { id: '2', highlight: 'Vibration science & Chakra balancing' },
  { id: '3', highlight: 'Sound Healing methods for individuals & groups' },
  { id: '4', highlight: 'Naturopathic Sound Systems' },
  { id: '5', highlight: 'Meditation & Mindfulness practice' },
  { id: '6', highlight: 'Practical workshop facilitation instruction' }],

  imageUrl: 'https://www.zenyoganepal.com/api/media/file/kughkjkbjlk%3B.png?prefix=uploads',
  imageAlt: 'Bowls ttc',
  index: 2,
  twinPrice: '$800',
  singlePrice: '$1200',
  fullDescription: [
  "Experience Nepal's timeless tradition of Himalayan sound healing through our own unique Buddha Bowls TTC course.",
  "You'll learn: Authentic Tibetan Bowl techniques, Vibration science, Chakra balancing, Bowl Healing techniques, Naturopathic Sound Systems, and Meditation & Mindfulness practice under experienced Masters.",
  'Our Teacher Training approach also develops your ability to guide individuals and groups with confidence through structured sessions and workshops.']

}];


interface CourseDetailModalProps {
  course: Course;
  onClose: () => void;
}

function CourseDetailModal({ course, onClose }: CourseDetailModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-dark/80 backdrop-blur-sm"
      onClick={onClose}>
      
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[2rem] bg-cream shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        
        <div className="relative h-56 overflow-hidden rounded-t-[2rem]">
          <Image
            src={course.imageUrl}
            alt={course.imageAlt}
            fill
            unoptimized
            className="object-cover" />
          
          <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/70 to-transparent"></div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white rounded-full p-2 hover:bg-white/40 transition-colors"
            aria-label="Close">
            
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
          <div className="absolute bottom-4 left-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-forest-dark/65 px-3 py-2 font-sans text-xs font-bold text-cream backdrop-blur-md">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" /><path d="M12 6v6h4" />
              </svg>
              {course.duration}
            </span>
          </div>
        </div>
        <div className="p-6 sm:p-8">
          <span className="w-fit rounded-full border border-forest/10 bg-cream-dark px-3 py-1.5 font-sans text-[10px] font-extrabold tracking-[0.14em] text-forest uppercase">
            Enrollment open
          </span>
          <h3 className="mt-4 font-serif text-2xl font-bold text-forest leading-tight">{course.title}</h3>
          <div className="mt-4 space-y-3 font-sans text-sm leading-relaxed text-charcoal/80">
            {course.fullDescription.map((para, i) =>
            <p key={i}>{para}</p>
            )}
          </div>
          <div className="mt-6">
            <h4 className="mb-3 font-sans text-xs font-extrabold tracking-[0.12em] text-forest uppercase">What you&apos;ll explore</h4>
            <ul className="space-y-2.5">
              {course.highlights.map((h) =>
              <li key={h.id} className="flex items-start gap-2.5">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0 text-saffron-dark" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />
                  </svg>
                  <span className="font-sans text-xs leading-relaxed text-charcoal/75 sm:text-sm">{h.highlight}</span>
                </li>
              )}
            </ul>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <a
              href="#registration"
              onClick={onClose}
              className="flex min-h-14 items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-saffron-light to-saffron-dark px-4 py-3 text-left font-sans text-white shadow-sm transition-shadow hover:shadow-lg">
              
              <span className="text-[11px] leading-tight font-bold">Twin sharing room</span>
              <span className="shrink-0 text-sm font-extrabold">{course.twinPrice}</span>
            </a>
            <a
              href="#registration"
              onClick={onClose}
              className="flex min-h-14 items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-saffron-light to-saffron-dark px-4 py-3 text-left font-sans text-white shadow-sm transition-shadow hover:shadow-lg">
              
              <span className="text-[11px] leading-tight font-bold">Single occupancy</span>
              <span className="shrink-0 text-sm font-extrabold">{course.singlePrice}</span>
            </a>
          </div>
        </div>
      </div>
    </div>);

}

export default function CoursesSection() {
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);

  return (
    <section
      id="courses"
      className="relative isolate scroll-mt-24 overflow-hidden bg-forest-dark py-16 text-cream sm:py-20 lg:py-24">
      
      {/* Decorative circles */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-48 -right-40 h-[34rem] w-[34rem] rounded-full border border-saffron-light/15"></div>
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-24 h-[22rem] w-[22rem] rounded-full border border-saffron-light/10"></div>
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 left-0 h-72 w-72 -translate-x-1/2 translate-y-1/2 rounded-full bg-saffron-light/10 blur-3xl"></div>

      <div className="relative mx-auto max-w-7xl px-4 md:px-8">
        {/* Header */}
        <header className="flex flex-col gap-7 md:flex-row md:items-end md:justify-between">
          <div className="max-w-4xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-saffron-light/25 bg-white/5 px-3 py-2 text-xs font-bold tracking-[0.15em] text-saffron-light uppercase">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
                <path d="M20 2v4" /><path d="M22 4h-4" />
                <circle cx="4" cy="20" r="2" />
              </svg>
              Choose your learning path
            </div>
            <h2 id="courses-heading" className="max-w-[20ch] text-balance font-serif text-[clamp(2.4rem,4.8vw,4.6rem)] leading-[1.03] font-bold tracking-[-0.035em] text-cream">
              Yoga TTC &amp; Singing Bowls
            </h2>
            <p className="mt-5 max-w-3xl font-sans text-sm leading-relaxed text-cream/70 sm:text-base lg:text-lg">
              Explore our intensive Yoga Alliance Certified teacher training courses and sound healing courses in Nepal. Learn authentic techniques, chakra balancing, and meditation from experienced Himalayan masters.
            </p>
          </div>
        </header>

        <p className="sr-only">Scroll horizontally to explore the available courses.</p>

        {/* Course Cards Rail */}
        <ul
          aria-labelledby="courses-heading"
          className="mt-10 flex items-stretch gap-6 overflow-x-auto overscroll-x-contain pb-5 md:mt-14 snap-x snap-proximity [scrollbar-color:var(--color-saffron-light)_rgba(255,255,255,0.08)] [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-saffron-light/75 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-white/10">
          
          {courses.map((course) =>
          <li
            key={course.id}
            className="w-[88%] sm:w-[72%] lg:w-[calc(50%-0.75rem)] shrink-0 snap-start">
            
              <article className="grid h-full min-h-[31rem] overflow-hidden rounded-[2rem] border border-white/10 bg-cream text-charcoal shadow-[0_30px_90px_-45px_rgba(0,0,0,0.9)] md:grid-cols-[42%_58%]">
                {/* Image Panel */}
                <button
                type="button"
                aria-label={`View details for ${course.title}`}
                onClick={() => setSelectedCourse(course)}
                className="group relative min-h-64 overflow-hidden bg-gradient-to-br from-forest via-forest to-forest-dark text-left focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-saffron-light md:min-h-full">
                
                  <Image
                  src={course.imageUrl}
                  alt={course.imageAlt}
                  fill
                  unoptimized
                  className="object-cover transition-transform duration-700 group-hover:scale-[1.035]" />
                
                  <span className="absolute inset-0 bg-gradient-to-t from-forest-dark/65 via-transparent to-forest-dark/15 transition-colors group-hover:from-forest-dark/75"></span>
                  <span className="absolute top-5 left-5 grid h-11 w-11 place-items-center rounded-full border border-white/25 bg-forest-dark/55 font-serif text-sm font-bold text-cream backdrop-blur-md">
                    {String(course.index).padStart(2, '0')}
                  </span>
                  <span className="absolute right-5 bottom-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-forest-dark/65 px-3 py-2 font-sans text-xs font-bold text-cream backdrop-blur-md">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" /><path d="M12 6v6h4" />
                    </svg>
                    {course.duration}
                  </span>
                </button>

                {/* Content Panel */}
                <div className="flex min-w-0 flex-col p-6 sm:p-8">
                  <span className="w-fit rounded-full border border-forest/10 bg-cream-dark px-3 py-1.5 font-sans text-[10px] font-extrabold tracking-[0.14em] text-forest uppercase">
                    Enrollment open
                  </span>
                  <h3 className="mt-5 text-left font-serif text-2xl leading-tight font-bold text-forest sm:text-3xl">
                    {course.title}
                  </h3>
                  <p className="mt-4 text-left font-sans text-sm leading-relaxed text-charcoal/70">
                    {course.shortDescription}
                  </p>

                  {/* Highlights */}
                  <div className="mt-6 text-left">
                    <h4 className="mb-3 font-sans text-xs font-extrabold tracking-[0.12em] text-forest uppercase">
                      What you&apos;ll explore
                    </h4>
                    <ul className="space-y-2.5">
                      {course.highlights.slice(0, 3).map((h) =>
                    <li key={h.id} className="flex items-start gap-2.5">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0 text-saffron-dark" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />
                          </svg>
                          <span className="font-sans text-xs leading-relaxed text-charcoal/75 sm:text-sm">{h.highlight}</span>
                        </li>
                    )}
                    </ul>
                    <p className="mt-3 pl-6 font-sans text-xs font-semibold text-forest/60">
                      +{course.highlights.length - 3} more in course details
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="mt-auto space-y-3 border-t border-forest/10 pt-6">
                    <button
                    type="button"
                    onClick={() => setSelectedCourse(course)}
                    className="flex w-full items-center justify-between rounded-xl border border-forest/20 px-4 py-3 text-left font-sans text-xs font-bold tracking-wide text-forest transition-colors hover:border-forest hover:bg-forest/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-saffron-dark sm:text-sm">
                    
                      View Course Details
                      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M7 7h10v10" /><path d="M7 17 17 7" />
                      </svg>
                    </button>
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(10.5rem,1fr))] gap-2">
                      <a
                      href="#registration"
                      className="flex min-h-14 items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-saffron-light to-saffron-dark px-4 py-3 text-left font-sans text-white shadow-sm transition-shadow hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-saffron-dark">
                      
                        <span className="text-[11px] leading-tight font-bold">Twin sharing room</span>
                        <span className="shrink-0 text-sm font-extrabold">{course.twinPrice}</span>
                      </a>
                      <a
                      href="#registration"
                      className="flex min-h-14 items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-saffron-light to-saffron-dark px-4 py-3 text-left font-sans text-white shadow-sm transition-shadow hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-saffron-dark">
                      
                        <span className="text-[11px] leading-tight font-bold">Single occupancy</span>
                        <span className="shrink-0 text-sm font-extrabold">{course.singlePrice}</span>
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            </li>
          )}
        </ul>
      </div>

      {/* Course Detail Modal */}
      {selectedCourse &&
      <CourseDetailModal
        course={selectedCourse}
        onClose={() => setSelectedCourse(null)} />

      }
    </section>);

}